'use client';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Image from 'next/image';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Bars3Icon,
  MapIcon,
  MapPinIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';

import { HeaderNavMenu } from './NavMenu';
import {
  updateUserLocation,
  hydrateUserLocation,
} from '@/redux/slices/userLocation.slice';
import ModalCart from './ModalCart';
import Modal from './Modal';
import LocationInput from './LocationInput';
import NavMenuDialog from './NavMenuDialog';
import { logoRed } from '@/assets/images';
import crunchies from '@/assets/CRUNCHIES..png';
import { useInView } from 'react-intersection-observer';
import { getUserLocation, isDeliveryTime } from '@/utils/helpers';
import AppStorage from '@/utils/helpers';
import { usePathname } from 'next/navigation';
import { PiHeartFill } from 'react-icons/pi';
import LocationDetails from './LocationDetails';
import { addToOutlet } from '@/redux/slices/category.slice';
import LocationWarning from './LocationWarning';

export default function Header({ sticky }: { sticky?: boolean }) {
  // State to track if component has mounted on client
  const [mounted, setMounted] = useState(false);

  // State to control visibility of modals
  const [viewModal, setViewModal] = useState({
    menu: false,
    location: false,
    cart: false,
    disableOrder: false,
  });

  const { ref, inView } = useInView({
    threshold: 0,
  });

  // State to store location data
  const [location, setLocation] = useState<any | null>(null);

  // Redux hooks to access and dispatch actions
  const locationState = useSelector(
    (state: any) => state.userLocation.location
  );
  const dispatch = useDispatch();
  const pathname = usePathname();

  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [branch, setBranch] = useState('');
  const [branches, setBranches] = useState<
    {
      _id: string;
      name: string;
      city: string;
      location: { coordinates: [number, number] };
      state?: string;
    }[]
  >([]);
  const [showWarning, setShowWarning] = useState<boolean>(false);

  // Set mounted state on client
  useEffect(() => {
    setMounted(true);
  }, []);

  // Hydrate location from AppStorage on initial render
  useEffect(() => {
    const storedLocation = JSON.parse(AppStorage.get('location') as string);
    if (storedLocation) {
      dispatch(hydrateUserLocation(storedLocation));
    }
  }, [dispatch]);

  // Update location state when locationState in Redux changes
  useEffect(() => {
    if (locationState) {
      setLocation(locationState);
      setViewModal((prev) => ({ ...prev, location: false }));
    } else if (pathname === '/' && !locationState) {
      setViewModal((prev) => ({ ...prev, location: true }));
    }
  }, [locationState]);

  // Cart state from Redux
  const { cart } = useSelector((state: any) => state.cart); // Accessing items array within cart

  // Calculate total number of items in cart
  const itemCount = cart?.reduce(
    (total: number, item: any) => total + item.quantity,
    0
  );
  const handleLocation = () => {
    const selectedBranch = branches.find(
      (branchObj: any) => branchObj.name === branch
    );
    console.log(selectedBranch);
    dispatch(addToOutlet(selectedBranch));

    const location = {
      id: selectedBranch?._id ?? '',
      address: selectedBranch?.name ?? '',
      street: '',
      city: selectedBranch?.city ?? '',
      name: selectedBranch?.city ?? '',
      state: selectedBranch?.state ?? '',
      lat: selectedBranch?.location?.coordinates[1] ?? 0,
      lng: selectedBranch?.location?.coordinates[0] ?? 0,
    };

    dispatch(hydrateUserLocation(location));
    AppStorage.save('location', JSON.stringify(location));
  };

  return (
    <header
      ref={ref}
      className={`px-5 pt-10 lg:py-5 bg-crunchies-white ${
        sticky && 'sticky top-0 z-50'
      } `}
    >
      <div className="w-full max-w-[1440px] mx-auto grid grid-cols-3 items-center gap-5 md:flex md:justify-between">
        {/* Logo */}
        <div className="gap-10 grid md:flex items-center w-max">
          <a href="/">
            <Image
              src={logoRed}
              // src={crunchies}
              alt="Crunchies Fried Chicken logo"
              className="w-20"
              priority
            />
          </a>

          {/* Location button */}
          <button
            className="py-3 w-max flex gap-2 items-center"
            onClick={() =>
              setViewModal((prev) => ({ ...prev, location: true }))
            }
          >
            <MapPinIcon className="w-6 text-crunchies-red" />
            <span className="text-xs w-32 md:w-full text-left capitalize">
              {mounted ? location?.name || 'Set location' : 'Set location'}
            </span>
          </button>

          {/* Location Modal */}
          <Modal
            closeModal={() =>
              setViewModal((prev) => ({ ...prev, location: false }))
            }
            openModal={viewModal.location}
            modalTitle="Hey Cruncher!"
            shouldCloseOnOverlayClick={false}
            closeButton
          >
            {/* <LocationInput /> */}
            <LocationDetails
              setState={setState}
              state={state}
              setCity={setCity}
              city={city}
              setBranch={setBranch}
              branch={branch}
              setBranches={setBranches}
              setShowWarning={setShowWarning}
            />
            <button
              disabled={!state || !city || !branch}
              className={`text-white flex gap-2 items-center shadow px-5 py-3 w-max mx-auto rounded-full ${!state || !city || !branch ? 'bg-gray-300' : 'bg-crunchies-red'}`}
              onClick={() => {
                // const newLocation = JSON.parse(
                //   AppStorage.get('location') as string
                // );
                // setLocation(newLocation);
                // dispatch(updateUserLocation(newLocation));
                handleLocation();
                setViewModal((prev) => ({ ...prev, location: false }));
              }}
            >
              <MapIcon className="w-6" />
              <span>Set location</span>
            </button>
          </Modal>
          {/* {showWarning && <LocationWarning setShowWarning={setShowWarning} />} */}
          <Modal
            openModal={showWarning}
            closeModal={() => setShowWarning(false)}
            modalTitle=""
            closeButton={true}
            shouldCloseOnOverlayClick={true}
          >
            <LocationWarning />
          </Modal>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:block">
          <HeaderNavMenu />
        </div>

        <div className={`col-span-2 grid gap-10  justify-end md:flex`}>
          {/* Order button */}
          {/* <a
            href={`/menu`}
            className={`flex text-white  gap-2 items-center bg-crunchies-red shadow px-5 py-3 w-max ml-auto rounded-xl`}
          >
            <ArrowRightIcon className="w-3 animate-ping" />
            <span className="font-bold">Order Now</span>

            <ArrowLeftIcon className="w-3 animate-ping" />
          </a> */}
          {/* <a
            href={`/valbox`}
            className={`flex gap-2 items-center text-sm lg:hidden w-[10.313rem] h-[2.3rem] rounded-[2.5rem]  border-[0.094rem] px-2 py-4  justify-center border-primary-500 text-primary-500 animate-pulse`}
          >
            <PiHeartFill className="w-6" />
            <div className="font-bold whitespace-nowrap">#LoveInABox</div>
          </a> */}

          <div className={` justify-between flex`}>
            {/* Shopping cart button */}
            <div
              className={`${
                mounted &&
                !inView &&
                'fixed top-1/3 right-0 p-2 rounded-l-full shadow-lg bg-crunchies-red z-10'
              }`}
            >
              <button
                className={`px-5 py-3 w-max relative 
                   ${pathname === '/valbox' ? 'hidden' : 'block'}
                  `}
                onClick={() =>
                  isDeliveryTime()
                    ? setViewModal((prev) => ({
                        ...prev,
                        cart: !viewModal.cart,
                      }))
                    : setViewModal((prev) => ({ ...prev, disableOrder: true }))
                }
              >
                {mounted && itemCount > 0 && (
                  <span
                    className={`absolute top-1 right-3 text-xs rounded-full w-5 h-5 flex items-center justify-center ${
                      inView
                        ? 'bg-crunchies-red text-crunchies-white'
                        : 'bg-crunchies-white text-crunchies-red'
                    }`}
                  >
                    {itemCount}
                  </span>
                )}
                <ShoppingCartIcon
                  className={`w-6 ${mounted && !inView ? 'text-white' : 'text-crunchies-red'}`}
                />
              </button>
              {/* Cart Modal */}
              <ModalCart
                open={viewModal.cart}
                closeModal={() =>
                  setViewModal((prev) => ({ ...prev, cart: false }))
                }
              />
            </div>

            {/* Mobile/Tablet Navigation button */}
            <button
              className="py-3 px-5 w-max lg:hidden"
              onClick={() => setViewModal((prev) => ({ ...prev, menu: true }))}
            >
              <Bars3Icon className="w-6 text-crunchies-red" />
            </button>
          </div>

          {/* Menu Dialog for mobile/tablet */}
          <NavMenuDialog
            openModal={viewModal.menu}
            closeModal={() =>
              setViewModal((prev) => ({ ...prev, menu: false }))
            }
          />
        </div>
      </div>

      {/* Disable order modal */}
      <Modal
        modalTitle="Oops..."
        openModal={viewModal.disableOrder}
        closeModal={() =>
          setViewModal((prev) => ({ ...prev, disableOrder: false }))
        }
      >
        <div>
          Dear customer, online order services are unavailable at this time. Our
          services will be active from 8am to 6pm. Thank you!
        </div>
        <button
          className="text-white flex gap-2 items-center bg-crunchies-red shadow px-5 py-3 w-max mx-auto rounded-full"
          onClick={() =>
            setViewModal((prev) => ({ ...prev, disableOrder: false }))
          }
        >
          <span>Okay! I&apos;ll wait</span>
        </button>
      </Modal>
    </header>
  );
}
